import { useState, type FormEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { ApiClientError } from '../api/http'
import { uploadNoonCatalog, type NoonCatalogImportJob } from '../channels/noonCatalogImportApi'
import type { ChannelConnection } from '../channels/types'
import { Button, Card, ChannelDot, I, TEXT_WRAP_STYLE, mobileButtonStyle } from '../components/ui'
import { useIsMobileViewport } from '../hooks/useIsMobileViewport'

interface Props {
  accessToken: string
  connection: ChannelConnection
  onClose: () => void
  onReviewUnmapped?: (connectionId: number) => void
  onJobCreated?: (job: NoonCatalogImportJob) => void
}

export function NoonCatalogImportPanel({ accessToken, connection, onClose, onReviewUnmapped, onJobCreated }: Props) {
  const { t } = useTranslation()
  const isMobile = useIsMobileViewport()
  const [file, setFile] = useState<File | null>(null)
  const [busy, setBusy] = useState(false)
  const [job, setJob] = useState<NoonCatalogImportJob | null>(null)
  const [error, setError] = useState<string | null>(null)
  const actionStyle = mobileButtonStyle(isMobile)

  const submit = async (event: FormEvent) => {
    event.preventDefault()
    if (busy) return
    setError(null)
    setJob(null)
    if (!file || !file.name.toLowerCase().endsWith('.xlsx') || file.size > 5 * 1024 * 1024) {
      setError(!file ? 'FILE_REQUIRED' : file.size > 5 * 1024 * 1024 ? 'FILE_TOO_LARGE' : 'FILE_TYPE_INVALID')
      return
    }
    setBusy(true)
    try {
      const created = await uploadNoonCatalog(accessToken, connection.id, file)
      setJob(created)
      onJobCreated?.(created)
    } catch (caught) {
      setError(caught instanceof ApiClientError ? caught.code.replace(/^NOON_CATALOG_IMPORT_/, '') : 'REQUEST_FAILED')
    } finally {
      setBusy(false)
    }
  }

  return (
    <Card style={{ marginBottom: 'var(--sp-4)', borderColor: 'var(--brand-300)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', gap: 'var(--sp-3)', flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', gap: 'var(--sp-2)', alignItems: 'center', minWidth: 0 }}>
          <ChannelDot name="Noon" size={30} />
          <h2 style={{ margin: 0, fontSize: 'var(--fs-16)', color: 'var(--fg)', ...TEXT_WRAP_STYLE }}>
            {t('channels.noonCatalogImport.title', { name: connection.displayName })}
          </h2>
        </div>
        <Button kind="ghost" size="xs" onClick={onClose} disabled={busy} style={actionStyle}>
          {t('channels.noonCatalogImport.close')}
        </Button>
      </div>
      <p style={{ fontSize: 'var(--fs-13)', color: 'var(--fg-muted)', ...TEXT_WRAP_STYLE }}>
        {t('channels.noonCatalogImport.description')}
      </p>
      <form onSubmit={submit} style={{ display: 'grid', gap: 'var(--sp-3)' }}>
        <label style={{ display: 'grid', gap: 'var(--sp-2)', fontSize: 'var(--fs-13)', minWidth: 0 }}>
          {t('channels.noonCatalogImport.fileLabel')}
          <input type="file" accept=".xlsx" disabled={busy}
            onChange={event => { setFile(event.target.files?.[0] ?? null); setError(null); setJob(null) }}
            style={{ width: '100%', minWidth: 0, boxSizing: 'border-box', padding: 'var(--sp-3)', fontFamily: 'inherit', color: 'var(--fg)', background: 'var(--bg-card)', border: '1px solid var(--border-default)', borderRadius: 'var(--r-sm)' }} />
        </label>
        <div style={{ fontSize: 'var(--fs-12)', color: 'var(--fg-muted)', ...TEXT_WRAP_STYLE }}>
          {t('channels.noonCatalogImport.fileHint')}
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button type="submit" kind="primary" size="sm" icon={I.download} disabled={busy} style={actionStyle}>
            {t(busy ? 'channels.noonCatalogImport.uploading' : 'channels.noonCatalogImport.upload')}
          </Button>
        </div>
      </form>
      {error && <p role="alert" style={{ color: 'var(--danger)', fontSize: 'var(--fs-13)', ...TEXT_WRAP_STYLE }}>
        {t(`channels.noonCatalogImport.errors.${error}`, { defaultValue: t('channels.noonCatalogImport.errors.REQUEST_FAILED') })}
      </p>}
      {job && <div style={{ display: 'grid', gap: 'var(--sp-3)', marginTop: 'var(--sp-4)' }}>
        <div role="status" style={{ fontSize: 'var(--fs-14)', fontWeight: 'var(--fw-bold)', color: 'var(--fg)' }}>
          {t('imports.queued', { id: job.id })}
        </div>
        {job.result && <><div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2, minmax(0, 1fr))' : 'repeat(4, minmax(0, 1fr))', gap: 'var(--sp-2)' }}>
          {(['imported', 'mapped', 'unmapped', 'skipped'] as const).map(key => (
            <div key={key} style={{ padding: 'var(--sp-3)', background: 'var(--bg)', borderRadius: 'var(--r-sm)', minWidth: 0 }}>
              <div style={{ fontSize: 'var(--fs-24)', fontWeight: 'var(--fw-bold)' }}>{job.result?.[key]}</div>
              <div style={{ fontSize: 'var(--fs-12)', color: 'var(--fg-muted)', ...TEXT_WRAP_STYLE }}>{t(`channels.noonCatalogImport.counts.${key}`)}</div>
            </div>
          ))}
        </div>
        {job.result.unmapped > 0 && onReviewUnmapped && <Button kind="secondary" size="sm" icon={I.link} onClick={() => onReviewUnmapped(connection.id)} style={actionStyle}>
          {t('channels.actions.reviewUnmappedShort')}
        </Button>}
        {job.result.issues.length > 0 && <details>
          <summary style={{ cursor: 'pointer', fontSize: 'var(--fs-13)' }}>{t('channels.noonCatalogImport.issues', { count: job.result.issues.length })}</summary>
          <ul style={{ maxHeight: 280, overflowY: 'auto', paddingInlineStart: 'var(--sp-5)', fontSize: 'var(--fs-12)' }}>
            {job.result.issues.map(issue => <li key={`${issue.row}-${issue.code}`} style={{ marginBlock: 'var(--sp-2)', ...TEXT_WRAP_STYLE }}>
              {t('channels.noonCatalogImport.row', { row: issue.row })}
              {issue.partnerSku && <> · <bdi style={{ fontFamily: 'var(--font-mono)' }}>{issue.partnerSku}</bdi></>}
              {' · '}{t(`channels.noonCatalogImport.issueCodes.${issue.code}`)}
            </li>)}
          </ul>
        </details>}</>}
      </div>}
    </Card>
  )
}
